import request from '@/utils/request'

/**
 * 用户相关 API
 */

// 获取用户信息（GET /member/personal/material；含 is_real：1 已实名 / 0 未实名，提现前须校验）
export const getUserInfo = () => {
  return request({
    url: '/member/personal/material',
    method: 'get'
  })
}

/**
 * 获取用户设置（回显）
 * 返回字段说明：
 * - speaker: 音色
 * - tts_provider: 发音源 microsoft（固定微软 Azure）
 * - speaker_volume: 发音人音量
 * - speaker_speed: 发音人语速
 * - speaker_auto_read: 切题后是否自动朗读
 * - speaker_answer_auto_read: 答题正确后自动朗读
 * - speaker_answer_tip_auto_read: 答案弹窗打开自动朗读
 * - answer_tip_word_cards_layout: 逐词卡片模式展示 toggle|stack，默认 stack
 * - input_word_chinese_hint: 输入框内逐词中文提示 0|1，默认 0
 * - answer_area_show_phonetic: 答题过程显示音标 0|1，默认 1（仅答题中生效；答案态/答案提示始终展示）
 * - answer_area_phonetic_scale: 答题过程音标字号倍数，默认 1；可选 1|1.2|1.5|1.8|2（仅答题中生效）
 * - answer_area_show_image: 练习区显示配图 0|1，默认 1（答题过程、卡片跟读、口语评测面板）
 * - answer_content_show_image: 答案区显示配图 0|1，默认 1（答案态、答案提示浮层）
 * - answer_area_show_pos_lamp: 词性聚光灯 0|1，默认 1（答案态/答案提示；关闭仅隐藏光锥与反射，保留词性标签与下划线）
 * - speaker_tone_enabled: 答题音效开关
 * - speaker_tone_right: 答对音效
 * - speaker_tone_error: 答错音效
 * - speaker_typing_tone_enabled: 打字音效开关
 * - speaker_typing_tone: 打字音效
 * - speaker_read_count: 朗读次数
 * - speaker_read_gap: 朗读间隔
 * - speaker_word_card_read: 逐词卡片点击朗读
 * - listening_auto_next_enabled: 句子听力模式自动切题 0|1，默认 0
 * - listening_auto_next_sec: 听力模式自动切题间隔（秒），建议 3|5|10|15|30|60，默认 5
 * - word_card_auto_next_enabled: 单词卡片跟读自动切题 0|1，默认 0
 * - word_card_auto_next_sec: 卡片跟读自动切题间隔（秒），同上，默认 5
 * - correct_auto_next_enabled: 输入类模式答对自动下一题 0|1，默认 0（开启后立即切题）
 * - correct_auto_master_enabled: 未开答案弹窗累计答对 5 次自动进已掌握 0|1，默认 0
 * - word_lookup_content_types: 词典内容类型（见下方「词典内容类型字段」）
 * - person_subject: 1白天 2黑夜
 * - person_bg_type: 背景类型 1背景色 2背景图
 * - person_bg_color: 背景颜色
 * - person_bg_image: 背景图片
 * - person_bg_mask_opacity: 背景图蒙版强度 0–100，默认 85（0=无蒙版，100=完全遮罩；仅背景图模式生效）
 * - person_font_color: 游戏字体颜色（空=跟随主题；勿用已废弃注释名 text_color）
 * - person_font_weight_title: 答题中文题干字重，字符串 "400"|"700"，默认 "400"；空/缺省前端按 400
 * - person_font_weight: 输入槽英文字重，字符串 "400"|"700"，默认 "400"；空/缺省前端按 400
 * - person_correct_color: 输入槽位聚焦态字母+下划线+阴影（空=跟随主题紫）
 * - person_answer_color: 答对槽位字母+下划线颜色（同一色；空=跟随主题绿）
 * - content_layout: 游戏内容布局 top|center|bottom
 * - sidebar_menu_layout: 左侧菜单对齐 column=图标在上 | row=图标文字同行，默认 row
 */

/**
 * 词典内容类型（set_show 回显 / set 保存 共用，仅此 1 个字段）
 *
 * 字段名：word_lookup_content_types
 * 类型：string
 * 必填：否
 * 默认：null / '' / 省略 —— 前端视为「全部类型启用、默认顺序」（并清除本地陈旧缓存）
 * 全部关闭：固定存 `_none`（与 null/'' 区分；查词页不展示任何可配置模块）
 *
 * 格式：竖线 | 分隔的内容类型 id，从左到右为查词页展示顺序；出现在串中=启用，未出现=关闭。
 * 示例：basic|web|syn|root ；全关：_none ；默认全开：'' 或 null
 *
 * id 枚举（展示名见 wordLookupSections.TAB_LABELS）：
 * basic, cn_dict, inflections, web, pro, ee, bilingual, native, authority,
 * phrase, syn, antonym, same, root, ety
 *
 * 存储建议：VARCHAR(512) 或 TEXT；入库前校验 id 均在枚举内，非法 id 丢弃。
 */
export const getUserSettings = (data) => {
  return request({
    url: '/member/personal/set_show',
    method: 'post',
    data
  })
}

/**
 * 保存用户设置
 * 参数说明：
 * - speaker: 音色
 * - tts_provider: 发音源 microsoft（固定微软 Azure）
 * - speaker_volume: 发音人音量
 * - speaker_speed: 发音人语速
 * - speaker_auto_read: 切题后是否自动朗读
 * - speaker_answer_auto_read: 答题正确后自动朗读
 * - speaker_answer_tip_auto_read: 答案弹窗打开自动朗读
 * - answer_tip_word_cards_layout: 逐词卡片模式展示 toggle|stack，默认 stack
 * - input_word_chinese_hint: 输入框内逐词中文提示 0|1，默认 0
 * - answer_area_show_phonetic: 答题过程显示音标 0|1，默认 1（仅答题中生效；答案态/答案提示始终展示）
 * - answer_area_phonetic_scale: 答题过程音标字号倍数，默认 1；可选 1|1.2|1.5|1.8|2（仅答题中生效）
 * - answer_area_show_image: 练习区显示配图 0|1，默认 1（答题过程、卡片跟读、口语评测面板）
 * - answer_content_show_image: 答案区显示配图 0|1，默认 1（答案态、答案提示浮层）
 * - answer_area_show_pos_lamp: 词性聚光灯 0|1，默认 1（答案态/答案提示；关闭仅隐藏光锥与反射，保留词性标签与下划线）
 * - speaker_tone_enabled: 答题音效开关
 * - speaker_tone_right: 答对音效
 * - speaker_tone_error: 答错音效
 * - speaker_typing_tone_enabled: 打字音效开关
 * - speaker_typing_tone: 打字音效
 * - speaker_read_count: 朗读次数
 * - speaker_read_gap: 朗读间隔
 * - speaker_word_card_read: 逐词卡片点击朗读
 * - listening_auto_next_enabled: 句子听力模式自动切题 0|1，默认 0
 * - listening_auto_next_sec: 听力模式自动切题间隔（秒），建议 3|5|10|15|30|60，默认 5
 * - word_card_auto_next_enabled: 单词卡片跟读自动切题 0|1，默认 0
 * - word_card_auto_next_sec: 卡片跟读自动切题间隔（秒），同上，默认 5
 * - correct_auto_next_enabled: 输入类模式答对自动下一题 0|1，默认 0（开启后立即切题）
 * - correct_auto_master_enabled: 未开答案弹窗累计答对 5 次自动进已掌握 0|1，默认 0
 * - word_lookup_content_types: 词典内容类型（见下方「词典内容类型字段」）
 * - person_subject: 1白天 2黑夜
 * - person_bg_type: 背景类型 1背景色 2背景图
 * - person_bg_color: 背景颜色
 * - person_bg_image: 背景图片
 * - person_bg_mask_opacity: 背景图蒙版强度 0–100，默认 85（0=无蒙版，100=完全遮罩；仅背景图模式生效）
 * - person_font_color: 游戏字体颜色（空=跟随主题）
 * - person_correct_color: 输入槽位聚焦态字母+下划线+阴影（空=跟随主题紫）
 * - person_answer_color: 答对槽位字母+下划线颜色（同一色；空=跟随主题绿）
 * - content_layout: 游戏内容布局 top|center|bottom
 * - sidebar_menu_layout: 左侧菜单对齐 column=图标在上 | row=图标文字同行，默认 row
 */
export const saveUserSettings = (data) => {
  return request({
    url: '/member/personal/set',
    method: 'post',
    data
  })
}